import React from 'react';
import { ALLOWED_USE_CASES, USE_CASE_LABELS, type UseCase } from '../../titleAnalysis/inputs';

/**
 * UseCaseSelect: the five title-analysis use cases.
 * Values and labels come from `titleAnalysis/inputs` so they stay in step
 * with validation and the backend.
 *
 * `variant="onInk"` renders for the dark guideline / use-case bar
 * (translucent fill, .24 border, white text).
 * Light theme only.
 */
export const USE_CASE_OPTIONS: ReadonlyArray<{ value: UseCase; label: string }> =
  ALLOWED_USE_CASES.map((value) => ({ value, label: USE_CASE_LABELS[value] }));

export interface UseCaseSelectProps {
  /** Selected use case, or '' / null / undefined for none. */
  value: UseCase | '' | null | undefined;
  onChange: (value: UseCase) => void;
  disabled?: boolean;
  /** Why the control is disabled; shown as a tooltip. */
  disabledReason?: string;
  variant?: 'light' | 'onInk';
  id?: string;
  /** Accessible name when no visible <label> points at the select. */
  ariaLabel?: string;
  className?: string;
}

const VARIANT_CLASSES = {
  light: 'border-gray-300 bg-white text-gray-900 focus:border-[#834DFB]',
  // Options render in the native popup, so keep them dark-on-white there.
  onInk:
    'border-white/25 bg-white/10 text-white focus:border-white [&>option]:text-gray-900',
} as const;

export const UseCaseSelect: React.FC<UseCaseSelectProps> = ({
  value,
  onChange,
  disabled = false,
  disabledReason,
  variant = 'light',
  id,
  ariaLabel = 'Use case',
  className = '',
}) => (
  <select
    id={id}
    aria-label={ariaLabel}
    value={value ?? ''}
    disabled={disabled}
    title={disabled ? disabledReason : undefined}
    onChange={(event) => {
      const next = event.target.value;
      if ((ALLOWED_USE_CASES as readonly string[]).includes(next)) {
        onChange(next as UseCase);
      }
    }}
    className={`h-9 rounded-lg border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50 ${VARIANT_CLASSES[variant]} ${className}`.trim()}
  >
    <option value="" disabled>
      Select a use case
    </option>
    {USE_CASE_OPTIONS.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

export default UseCaseSelect;
